import { Modal } from '@citizen-walk/shared-ui';
import { useMemo } from 'react';
import { usePhotoUrl, type PostMinimal } from '../posts';
import { IdeaWorkspaceMiniMap } from './IdeaWorkspaceMiniMap';
import { sentimentToHsl } from './sentimentColor';
import type { IdeaMinimal } from './useEcwIdeas';
import type { InsightMinimal } from './useEcwInsights';

/**
 * 気づきカード詳細 modal (Sprint X5e、R-38 follow-up、旧 InsightList 詳細画面相当)。
 *
 * X5c の minimum InsightCard では本文 + 紐づく投稿 button のみだったため、
 * 旧の詳細画面で提供されていた以下 2 要素を独立 modal として補完:
 *   - 紐づく投稿の MapLibre 地図 (selectedPostIds 該当 post の location pin)
 *   - 紐づく投稿の写真 grid (usePhotoUrl 経由で表示)
 *
 * docs/107 Wave8c で old2 `InsightList.tsx` (L143-268) の詳細画面 parity に拡張:
 *   - **番号付き写真グリッド** (old2 L184-205「📋 ベースとなった市民データ」):
 *     selectedPostIds 該当 post を 120px 幅カードで横並び、タイトル左に丸番号 badge (idx+1)。
 *   - **地図ピン番号** (old2 L102-141): 写真グリッドと同じ番号の付いたピンを持つミニマップ。
 *     `IdeaWorkspaceMiniMap` mode='front' を再利用 (Wave8b 処方箋詳細と同パターン、
 *     selectedPosts を 1..N 番号付き marker で配置 + 自動 fitBounds)。
 *   - **メタ (createdAt)** (old2 L251-256): 投稿者 / チーム / 作成日時 (toLocaleString ja-JP)。
 *
 * 旧 InsightList との差分:
 *   - 旧は full-page route 遷移、v2 は Modal で AnalysisInsightView 内に重ね表示
 *   - 編集機能は scope 外 (本人のみ削除のみ、X5c minimum 踏襲)
 */

export interface InsightDetailModalProps {
  open: boolean;
  onClose: () => void;
  insight: InsightMinimal | null;
  posts: PostMinimal[];
  /** docs/107 Wave8: 関連する処方箋の有無判定 + 「処方箋を見る」遷移に使用。 */
  ideas?: IdeaMinimal[];
  onShowRelatedIdeas?: (insight: InsightMinimal) => void;
  /** 投稿写真クリック時に PostDetailModal を呼ぶ contractor (任意)。 */
  onSelectPost?: (post: PostMinimal) => void;
  /** docs/107 Wave8c: 番号付きミニマップの pmtiles 解決に使用 (未指定なら OSM fallback)。 */
  districtId?: string;
  /** sessionId から解決したセッション名 (内部 ID は出さない)。未解決/無紐付けは null。 */
  sessionName?: string | null;
}

// Sprint AW: sentimentToHsl を共通 helper に統合 (旧 L30-34)。

export function InsightDetailModal({
  open,
  onClose,
  insight,
  posts,
  ideas = [],
  onShowRelatedIdeas,
  onSelectPost,
  districtId = '',
  sessionName,
}: InsightDetailModalProps) {
  // docs/107 Wave8 / old2 InsightList.tsx:166-181 — この気づきをベースにした処方箋があるか。
  const hasRelatedIdeas = useMemo(
    () =>
      insight !== null &&
      ideas.some(
        (idea) => idea.insightIds.includes(insight.id) || insight.id === `virtual_${idea.id}`,
      ),
    [insight, ideas],
  );
  // old2 L144/L317: insight.selectedPostIds 順で post を解決 (番号 badge / マーカー番号の基準)。
  const linkedPosts = useMemo(
    () => (insight ? posts.filter((p) => insight.selectedPostIds.includes(p.id)) : []),
    [insight, posts],
  );
  const postsWithLocation = useMemo(
    () => linkedPosts.filter((p) => p.location !== null),
    [linkedPosts],
  );

  if (!insight) return null;

  return (
    <Modal open={open} onClose={onClose} title="気づきカード詳細" size="lg">
      <div
        className="rounded-lg border-2 p-3 mb-4"
        style={{ borderColor: sentimentToHsl(insight.sentiment) }}
      >
        <h3 className="font-semibold text-base text-slate-900 break-words">{insight.title}</h3>
        <p className="text-xs text-slate-600 mt-1">
          {sessionName ? `セッション: ${sessionName} · ` : ''}
          {insight.team ? `検討チーム: ${insight.team} · ` : ''}by {insight.nickname || '匿名'} ·
          sentiment {insight.sentiment.toFixed(2)}
        </p>
      </div>

      {/* docs/107 Wave8 / old2 InsightList.tsx:166-181 — 関連処方箋がある場合のみ表示。 */}
      {onShowRelatedIdeas && hasRelatedIdeas && (
        <button
          type="button"
          onClick={() => onShowRelatedIdeas(insight)}
          className="mb-4 w-full text-sm px-3 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 transition-colors text-left"
        >
          🔗 この気づきをベースにした処方箋を見る →
        </button>
      )}

      {insight.observations && (
        <section className="mb-3">
          <h4 className="text-xs font-semibold text-slate-700 mb-1">観察した事実</h4>
          <p className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 rounded p-2 border border-slate-200">
            {insight.observations}
          </p>
        </section>
      )}

      {insight.diagnosis && (
        <section className="mb-3">
          <h4 className="text-xs font-semibold text-slate-700 mb-1">診断・解釈</h4>
          <p className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 rounded p-2 border border-slate-200">
            {insight.diagnosis}
          </p>
        </section>
      )}

      {/* Phase B+1.3: AI 対話ログ section (旧 InsightList L207-249 踏襲) */}
      {(insight.initialThoughts ||
        insight.llmAnalysis ||
        (insight.llmResolutionQuestions && insight.llmResolutionQuestions.length > 0) ||
        (insight.llmDiagnosisQuestions && insight.llmDiagnosisQuestions.length > 0)) && (
        <section className="mb-3">
          <h4 className="text-xs font-semibold text-primary-700 mb-2">🤖 AI との対話ログ</h4>
          <div className="space-y-2 text-xs">
            {insight.initialThoughts && insight.initialThoughts.trim().length > 0 && (
              <div className="rounded p-2 bg-amber-50 border border-amber-200">
                <div className="font-semibold text-amber-800 mb-1">
                  ✍️ STEP 0: 参加者の最初の感想
                </div>
                <p className="whitespace-pre-wrap text-slate-800">{insight.initialThoughts}</p>
              </div>
            )}
            {insight.llmAnalysis && (
              <div className="rounded p-2 bg-slate-50 border border-slate-200">
                <div className="font-semibold text-primary-700 mb-1">STEP 1: AI からの所見</div>
                <p className="whitespace-pre-wrap text-slate-800">{insight.llmAnalysis}</p>
              </div>
            )}
            {insight.llmResolutionQuestions && insight.llmResolutionQuestions.length > 0 && (
              <div className="rounded p-2 bg-slate-50 border border-slate-200">
                <div className="font-semibold text-primary-700 mb-1">STEP 2: AI からの問いかけ</div>
                <ul className="list-disc pl-5 space-y-0.5 text-slate-800">
                  {insight.llmResolutionQuestions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </div>
            )}
            {insight.observations && (
              <div className="rounded p-2 bg-amber-50 border border-amber-200">
                <div className="font-semibold text-amber-800 mb-1">✍️ 参加者の回答 (症状)</div>
                <p className="whitespace-pre-wrap text-slate-800">{insight.observations}</p>
              </div>
            )}
            {insight.llmDiagnosisQuestions && insight.llmDiagnosisQuestions.length > 0 && (
              <div className="rounded p-2 bg-slate-50 border border-slate-200">
                <div className="font-semibold text-primary-700 mb-1">STEP 3: AI からの問いかけ</div>
                <ul className="list-disc pl-5 space-y-0.5 text-slate-800">
                  {insight.llmDiagnosisQuestions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </div>
            )}
            {insight.diagnosis && (
              <div className="rounded p-2 bg-amber-50 border border-amber-200">
                <div className="font-semibold text-amber-800 mb-1">✍️ 参加者の回答 (診断)</div>
                <p className="whitespace-pre-wrap text-slate-800">{insight.diagnosis}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* docs/107 Wave8c / old2 InsightList.tsx:183-205 — 📋 ベースとなった市民データ。
          番号付き写真グリッド (idx+1 丸 badge) + 同じ番号のピンを持つミニマップ。 */}
      {linkedPosts.length > 0 ? (
        <section className="mb-3">
          <h4 className="text-xs font-semibold text-primary-700 mb-2">
            📋 ベースとなった市民データ ({linkedPosts.length} 件)
          </h4>
          {/* 番号付き写真グリッド (old2 L186-200) */}
          <div className="flex flex-wrap gap-2">
            {linkedPosts.map((p, idx) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelectPost?.(p)}
                className="w-[120px] rounded-md border border-slate-200 overflow-hidden bg-white text-left hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                aria-label={`投稿「${p.title || '無題'}」(${idx + 1}番) を開く`}
                title={p.title || '(無題)'}
              >
                <NumberedPostThumb post={p} />
                <div className="px-1.5 py-1 text-[10px] font-semibold text-slate-700 truncate flex items-center gap-1">
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 shrink-0 rounded-full bg-primary-500 text-white text-[8px] leading-none">
                    {idx + 1}
                  </span>
                  <span className="truncate">{p.title || '(無題)'}</span>
                </div>
              </button>
            ))}
          </div>
          {/* 地図ピン番号: 写真グリッドと同じ番号のピンを持つミニマップ (old2 L202-204 + L102-141)。
              IdeaWorkspaceMiniMap mode='front' を再利用 (selectedPosts を 1..N 番号付き marker)。 */}
          {postsWithLocation.length > 0 && (
            <div className="mt-3">
              <IdeaWorkspaceMiniMap
                districtId={districtId}
                mode="front"
                selectedPosts={linkedPosts}
                onSelectPost={onSelectPost}
                className="w-full h-[250px] rounded-lg border border-slate-200 overflow-hidden bg-slate-100"
              />
            </div>
          )}
        </section>
      ) : (
        <p className="text-xs text-slate-500 italic mb-3">紐づく投稿はありません</p>
      )}

      {/* docs/107 Wave8c / old2 InsightList.tsx:251-256 — メタ情報 (投稿者 / チーム / 作成日時)。 */}
      <div className="pt-3 mt-1 border-t border-slate-200 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
        <span>投稿者: {insight.nickname || '匿名'}</span>
        {sessionName && <span>セッション: {sessionName}</span>}
        {insight.team && <span>チーム: {insight.team}</span>}
        {insight.createdAt && <span>{formatCreatedAt(insight.createdAt)}</span>}
      </div>
    </Modal>
  );
}

/**
 * docs/107 Wave8c / old2 InsightList.tsx:255 — createdAt を `toLocaleString('ja-JP')` で整形。
 * Firestore Timestamp の toDate() を使う (null は呼出側でガード済)。
 */
function formatCreatedAt(ts: InsightMinimal['createdAt']): string {
  if (!ts) return '';
  try {
    return ts.toDate().toLocaleString('ja-JP');
  } catch {
    return '';
  }
}

/**
 * docs/107 Wave8c — 番号付きグリッド用の写真サムネ (old2 L189-193、120px カードの上部)。
 * usePhotoUrl で doc 保存済 URL + オフライン fallback。未取得/失敗時は 📷 placeholder。
 */
function NumberedPostThumb({ post }: { post: PostMinimal }) {
  const photo = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl);
  if (photo.status === 'ready' && photo.url) {
    return (
      <img
        src={photo.url}
        alt={post.title || '投稿写真'}
        loading="lazy"
        className="w-full h-20 object-contain bg-slate-100"
      />
    );
  }
  return (
    <span
      aria-label={photo.status === 'failed' ? '写真の読込に失敗' : '写真読込中'}
      className={`flex items-center justify-center w-full h-20 bg-slate-100 text-slate-400 text-lg ${photo.status === 'loading' ? 'animate-pulse' : ''}`}
    >
      📷
    </span>
  );
}
